if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.submitButton = this.querySelector('[type="submit"]');
      this.cart = document.querySelector('cart-drawer');
      this.hideErrors = this.dataset.hideErrors === 'true';

      // Buy Now button (dynamic checkout replacement)
      this.buyNowButton = this.querySelector('[data-buy-now]');
      if (this.buyNowButton) {
        this.buyNowButton.addEventListener('click', this.onBuyNowHandler.bind(this));
      }
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('disabled') === 'true') return;
      this.handleErrorMessage();
      this.submitButton.setAttribute('disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading--spinner')?.classList.remove('hidden');
      this.submitButton.querySelector('[data-add-to-cart-text]').classList.add('hidden');

      const addonEl = document.querySelector('addon-product');
      const formData = new FormData(this.form);

      if (addonEl && addonEl.hasSelections && addonEl.hasSelections()) {
        this.addBundleSequentially(addonEl, formData);
      } else {
        this.addSingleItem(formData);
      }
    }

    // ---------------------------------------------------------------------
    // BUY NOW
    // ---------------------------------------------------------------------
    onBuyNowHandler(evt) {
      evt.preventDefault();
      if (this.buyNowButton.getAttribute('disabled') === 'true') return;
      this.handleErrorMessage();
      this.buyNowButton.setAttribute('disabled', true);
      this.buyNowButton.classList.add('loading');

      const addonEl = document.querySelector('addon-product');
      const formData = new FormData(this.form);

      if (addonEl && addonEl.hasSelections && addonEl.hasSelections()) {
        this.addBundleSequentially(addonEl, formData, { redirectToCheckout: true });
      } else {
        this.addSingleItem(formData, { redirectToCheckout: true });
      }
    }

    resetBuyNowButton() {
      if (!this.buyNowButton) return;
      this.buyNowButton.classList.remove('loading');
      if (!this.error) this.buyNowButton.removeAttribute('disabled');
    }

    // Step 1: add the main product on its own. Step 2 (only on success): add the addon items,
    // linked to the main line via the same _bundle_id property.
    // options.redirectToCheckout: true -> called from Buy Now, skip cart drawer, go to /checkout
    addBundleSequentially(addonEl, formData, options = {}) {
      const { redirectToCheckout = false } = options;
      const bundleId = `bundle_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      formData.set('properties[_bundle_id]', bundleId);

      const mainConfig = fetchConfig('javascript');
      mainConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete mainConfig.headers['Content-Type'];
      mainConfig.body = formData;

      fetch(`${routes.cart_add_url}`, mainConfig)
        .then((response) => response.json())
        .then((mainResponse) => {
          if (mainResponse.status) {
            this.handleAddToCartError(mainResponse, formData);
            return;
          }

          // Main product added successfully — now add the addon items
          const addonItems = addonEl.getSelectedItems().map((item) => ({
            id: item.id,
            quantity: 1,
            properties: { _bundle_id: bundleId, _bundle_addon_name: item.name }
          }));

          const addonPayload = { items: addonItems };
          if (this.cart && !redirectToCheckout) {
            addonPayload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            addonPayload.sections_url = window.location.pathname;
          }

          return fetch(`${routes.cart_add_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(addonPayload)
          })
            .then((response) => response.json())
            .then((addonResponse) => {
              if (addonResponse.status) {
                this.handleAddToCartError(addonResponse, formData);
                return;
              }

              if (redirectToCheckout) {
                window.location.href = '/checkout';
                return;
              }

              this.handleAddToCartSuccess(addonResponse, formData);
            });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          if (redirectToCheckout) {
            this.resetBuyNowButton();
          } else {
            this.resetSubmitButton();
          }
        });
    }

    // Original single-item flow, extended with an optional checkout redirect for Buy Now
    addSingleItem(formData, options = {}) {
      const { redirectToCheckout = false } = options;
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      if (this.cart && !redirectToCheckout) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleAddToCartError(response, formData);
            return;
          }

          if (redirectToCheckout) {
            window.location.href = '/checkout';
            return;
          }

          this.handleAddToCartSuccess(response, formData);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          if (redirectToCheckout) {
            this.resetBuyNowButton();
          } else {
            this.resetSubmitButton();
          }
        });
    }

    handleAddToCartError(response, formData) {
      publish(PUB_SUB_EVENTS.cartError, {
        source: 'product-form',
        productVariantId: formData.get('id'),
        errors: response.description,
        message: response.message
      });
      this.handleErrorMessage(response.description);
      this.submitButton.setAttribute('disabled', true);
      if (this.buyNowButton) this.buyNowButton.setAttribute('disabled', true);
      this.error = true;
      const drawerSelector = document.querySelector('#quickView-drawer');
      setTimeout(() => {
        trapFocusElements(drawerSelector);
      }, 500);
    }

    handleAddToCartSuccess(response, formData) {
      if (!this.cart) {
        window.location = window.routes.cart_url;
        return;
      }
      if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id') });
      this.error = false;

      if (document.querySelector('.product-sticky-bar')) {
        document.querySelector('.product-sticky-bar').classList.remove('is-visible');
      }
      this.cart.renderContents(response);
      this.cart.classList.add('is-visible');
      focusElement = this.submitButton;
      document.querySelector('body').classList.add('overflow-hidden');
    }

    resetSubmitButton() {
      this.submitButton.classList.remove('loading');
      this.submitButton.querySelector('[data-add-to-cart-text]').classList.remove('hidden');
      if (this.cart && this.cart.classList.contains('is-empty'))
        this.cart.classList.remove('is-empty');
      if (!this.error) this.submitButton.removeAttribute('disabled');
      this.querySelector('.loading--spinner')?.classList.add('hidden');
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form--error-message-wrapper') || this.closest("product-card").querySelector('.product-form--error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form--error-message') || this.closest("product-card").querySelector('.product-form--error-message-wrapper');
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}